import "@hotwired/turbo-rails"
import { createConsumer, type Subscription } from "@rails/actioncable"
import React from "react"
import { CheckCircle2 } from "lucide-react"
import "../ui/primitives.css"
import "../ui/base_ui_extras.css"
import "../ui/advanced_primitives.css"
import "../ui/forms.css"
import "../ui/shell.css"
import "../ui/product_surfaces.css"
import { mountReactIsland, unmountReactIslands } from "../ui/islands"
import { mountShellUi } from "../ui/shell"
import { Toaster } from "../ui/primitives/toast"

function ReactSanity() {
  return (
    <div className="react-card">
      <CheckCircle2 size={20} />
      <span>React + TypeScript + Vite are alive.</span>
    </div>
  )
}

let sanityConsumer: ReturnType<typeof createConsumer> | null = null
let sanitySubscription: Subscription | null = null
let showcaseStylesPromise: Promise<unknown> | null = null
const developmentFrontend = import.meta.env.DEV || import.meta.env.VITE_RAILS_ENV === "development"

function disconnectCableSanity() {
  sanityConsumer?.disconnect()
  sanityConsumer = null
  sanitySubscription = null
}

function mountCableSanity() {
  const status = document.querySelector<HTMLElement>("#cable-status")
  const message = document.querySelector<HTMLElement>("#cable-message")
  const button = document.querySelector<HTMLButtonElement>("#cable-broadcast")

  disconnectCableSanity()
  if (!status || !message || !button) return

  sanityConsumer = createConsumer()
  sanitySubscription = sanityConsumer.subscriptions.create("SanityChannel", {
    connected() {
      status.textContent = "Connected to Action Cable."
      button.disabled = false
    },
    disconnected() {
      status.textContent = "Disconnected from Action Cable."
      button.disabled = true
    },
    rejected() {
      status.textContent = "Action Cable subscription rejected. Sign in and reload this page."
      button.disabled = true
    },
    received(data: { message?: string; at?: string }) {
      message.textContent = `${data.message || "Broadcast received"}${data.at ? ` at ${data.at}` : ""}`
    },
  })

  button.disabled = true
  button.onclick = () => sanitySubscription?.perform("broadcast")
}

function loadDevelopmentShowcaseStyles() {
  showcaseStylesPromise ||= import("../ui/showcase.css")
  return showcaseStylesPromise
}

async function mountUiGallery() {
  if (!developmentFrontend || !document.querySelector("#ui-gallery")) return

  await loadDevelopmentShowcaseStyles()
  const { UiGallery } = await import("../ui/gallery")
  if (!document.querySelector("#ui-gallery")) return

  mountReactIsland("#ui-gallery", () => <UiGallery />)
}

async function mountDevelopmentDemos() {
  if (!developmentFrontend || !document.querySelector("[data-demo], [data-demo-index]")) return

  await loadDevelopmentShowcaseStyles()
  const { mountDemoPages } = await import("../demos")
  await mountDemoPages()
}

async function mountSemanticInputs() {
  if (!document.querySelector("[data-semantic-input]")) return
  const semantic = await import("../ui/semantic_inputs")
  semantic.mountSemanticInputs()
}

async function mountInvoiceDraftUi() {
  if (!document.querySelector("[data-invoice-line-items]")) return
  const invoiceDrafts = await import("../ui/invoice_drafts")
  invoiceDrafts.mountInvoiceDraftLineItems()
}

document.addEventListener("turbo:load", () => {
  mountShellUi()
  mountReactIsland("#toast-root", () => <Toaster />)
  mountReactIsland("#react-sanity", () => <ReactSanity />)
  void mountUiGallery()
  void mountDevelopmentDemos()
  void mountSemanticInputs()
  void mountInvoiceDraftUi()
  mountCableSanity()
})

document.addEventListener("turbo:before-cache", () => {
  disconnectCableSanity()
  unmountReactIslands()
})
